import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, Collapse, Avatar, Menu,
  MenuItem, Divider, Badge, Chip, alpha, Tooltip, InputBase, Select, FormControl
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, People, Business, Receipt, AttachMoney,
  Assignment, Description, Assessment, Settings, ExpandLess, ExpandMore,
  PersonAdd, AccountCircle, Logout, Schedule, VerifiedUser,
  Notifications, Search, Payment, SupervisorAccount, Security, SwapHoriz
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { companiesAPI } from '../../services/api';

const drawerWidth = 260;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, selectedCompanyId, switchCompany } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({ Parties: true, Billing: true });
  const [anchorEl, setAnchorEl] = useState(null);
  const [companies, setCompanies] = useState([]);

  const isSuperAdmin = user?.isSuperAdmin || user?.role === 'super_admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';
  const perms = user?.permissions || {};

  // Load all companies for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      companiesAPI.getAll().then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCompanies(list);
        // Auto-select user's own company if none selected
        if (!selectedCompanyId && list.length > 0) {
          const own = list.find(c => c.id === user?.companyId || c._id === user?.companyId);
          if (own) switchCompany(String(own.id || own._id));
        }
      }).catch(() => {});
    }
  }, [isSuperAdmin, selectedCompanyId, user?.companyId, switchCompany]);

  // Build menu dynamically based on permissions
  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/', color: '#3b82f6' },
    ...(isSuperAdmin ? [{ text: 'Tenant Manager', icon: <SupervisorAccount />, path: '/admin', color: '#dc2626' }] : []),
    {
      text: 'Parties', icon: <People />, color: '#8b5cf6', children: [
        ...(perms.providers?.canView !== false ? [{ text: 'Providers', icon: <Business />, path: '/providers' }] : []),
        ...(perms.contractors?.canView !== false ? [{ text: 'Contractors', icon: <PersonAdd />, path: '/contractors' }] : []),
        { text: 'Assignments', icon: <Assignment />, path: '/assignments' },
      ].filter(Boolean)
    },
    { text: 'Collections', icon: <AttachMoney />, path: '/collections', color: '#f59e0b' },
    ...(perms.billing?.canView !== false || isAdmin ? [{
      text: 'Billing', icon: <Receipt />, color: '#10b981', children: [
        { text: 'Invoices', icon: <Receipt />, path: '/invoices' },
        { text: 'Payments', icon: <Payment />, path: '/payments' },
      ]
    }] : []),
    ...(perms.credentialing?.canView !== false || isAdmin ? [{ text: 'Credentialing', icon: <VerifiedUser />, path: '/credentialing', color: '#06b6d4' }] : []),
    { text: 'Tasks', icon: <Assignment />, path: '/tasks', color: '#ec4899' },
    { text: 'Documents', icon: <Description />, path: '/documents', color: '#64748b' },
    ...(perms.reports?.canView !== false || isAdmin ? [{ text: 'Reports', icon: <Assessment />, path: '/reports', color: '#f97316' }] : []),
    { text: 'Invoice Hub', icon: <Schedule />, path: '/scheduler', color: '#14b8a6' },
    ...(isAdmin ? [{ text: 'Settings', icon: <Settings />, path: '/settings', color: '#6b7280' }] : []),
    { text: 'Security', icon: <Security />, path: '/security', color: '#dc2626' },
  ].filter(i => !i.children || i.children.length > 0);

  const handleMenuClick = (text) => setOpenMenus(prev => ({ ...prev, [text]: !prev[text] }));
  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) => children?.some(c => location.pathname === c.path);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)' }}>
      {/* Logo */}
      <Box sx={{
        px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2.5,
          background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(59,130,246,0.35)'
        }}>
          <AttachMoney sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#f8fafc', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            MyPayPlus
          </Typography>
          <Typography sx={{ fontSize: '0.58rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
            Billing Platform
          </Typography>
        </Box>
      </Box>

      {/* Nav Items */}
      <List sx={{ flex: 1, px: 1.5, py: 1.5, overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.children ? (
              <>
                <ListItemButton
                  onClick={() => handleMenuClick(item.text)}
                  sx={{
                    borderRadius: 2, mb: 0.3, py: 0.8, px: 1.5,
                    color: isParentActive(item.children) ? '#e2e8f0' : '#94a3b8',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#e2e8f0' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: isParentActive(item.children) ? item.color : 'inherit' }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 19 } })}
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }} />
                  {openMenus[item.text] ?
                    <ExpandLess sx={{ fontSize: 16, opacity: 0.5 }} /> :
                    <ExpandMore sx={{ fontSize: 16, opacity: 0.5 }} />}
                </ListItemButton>
                <Collapse in={openMenus[item.text]} timeout="auto">
                  <List disablePadding sx={{ ml: 0.5 }}>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.text}
                        onClick={() => navigate(child.path)}
                        sx={{
                          borderRadius: 2, mb: 0.2, py: 0.6, pl: 3.5,
                          position: 'relative',
                          color: isActive(child.path) ? '#f8fafc' : '#94a3b8',
                          bgcolor: isActive(child.path) ? alpha(item.color || '#3b82f6', 0.12) : 'transparent',
                          '&:hover': { bgcolor: isActive(child.path) ? alpha(item.color || '#3b82f6', 0.18) : 'rgba(255,255,255,0.03)', color: '#e2e8f0' },
                          '&::before': isActive(child.path) ? {
                            content: '""', position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                            width: 5, height: 5, borderRadius: '50%', bgcolor: item.color || '#3b82f6'
                          } : {},
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 26, color: 'inherit' }}>
                          {React.cloneElement(child.icon, { sx: { fontSize: 16 } })}
                        </ListItemIcon>
                        <ListItemText primary={child.text} primaryTypographyProps={{
                          fontSize: 12.5, fontWeight: isActive(child.path) ? 600 : 400
                        }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2, mb: 0.3, py: 0.8, px: 1.5,
                  position: 'relative',
                  color: isActive(item.path) ? '#f8fafc' : '#94a3b8',
                  bgcolor: isActive(item.path) ? alpha(item.color || '#3b82f6', 0.12) : 'transparent',
                  '&:hover': { bgcolor: isActive(item.path) ? alpha(item.color || '#3b82f6', 0.18) : 'rgba(255,255,255,0.04)', color: '#e2e8f0' },
                  '&::before': isActive(item.path) ? {
                    content: '""', position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                    width: 3, height: 18, borderRadius: '0 4px 4px 0', bgcolor: item.color || '#3b82f6'
                  } : {},
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: isActive(item.path) ? item.color || '#3b82f6' : 'inherit' }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 19 } })}
                </ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{
                  fontSize: 13, fontWeight: isActive(item.path) ? 700 : 500, letterSpacing: '-0.01em'
                }} />
                {item.text === 'Tenant Manager' && (
                  <Chip label="SA" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: alpha('#dc2626', 0.2), color: '#f87171' }} />
                )}
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* User Info Bottom */}
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
          borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.04)'
        }}>
          <Avatar sx={{
            width: 34, height: 34, fontSize: 13, fontWeight: 700,
            background: isSuperAdmin ? 'linear-gradient(135deg, #dc2626, #f97316)' : 'linear-gradient(135deg, #3b82f6, #6366f1)'
          }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: '#f8fafc' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography noWrap sx={{ fontSize: 10.5, color: isSuperAdmin ? '#f87171' : '#64748b', fontWeight: isSuperAdmin ? 600 : 400 }}>
              {isSuperAdmin ? 'Super Admin' : (user?.role || '').replace('_', ' ')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Top Bar */}
      <AppBar position="fixed" sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)',
        color: '#0f172a', borderBottom: '1px solid #e2e8f0',
        boxShadow: 'none'
      }}>
        <Toolbar sx={{ gap: 1, minHeight: '56px !important' }}>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>

          {/* Search */}
          <Box sx={{
            display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9',
            borderRadius: 2.5, px: 2, py: 0.4, flex: 1, maxWidth: 360
          }}>
            <Search sx={{ fontSize: 18, color: '#94a3b8', mr: 1 }} />
            <InputBase placeholder="Search..." sx={{ flex: 1, fontSize: 13 }} />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {isSuperAdmin && companies.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
              <Select
                value={selectedCompanyId || ''}
                onChange={(e) => { switchCompany(e.target.value); window.location.reload(); }}
                displayEmpty
                startAdornment={<SwapHoriz sx={{ fontSize: 18, color: '#3b82f6', mr: 0.5 }} />}
                sx={{
                  fontSize: '0.82rem', fontWeight: 600, height: 36,
                  bgcolor: alpha('#3b82f6', 0.06), borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#3b82f6', 0.2) },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                }}
              >
                {companies.map(c => (
                  <MenuItem key={c.id || c._id} value={String(c.id || c._id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ fontSize: 16, color: '#3b82f6' }} />
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{c.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isSuperAdmin && (
            <Chip label="SUPER ADMIN" size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800, bgcolor: alpha('#dc2626', 0.1), color: '#dc2626', mr: 1 }} />
          )}

          <Tooltip title="Notifications">
            <IconButton size="small" sx={{ bgcolor: '#f1f5f9', mr: 0.5 }}>
              <Badge badgeContent={3} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16 } }}>
                <Notifications sx={{ fontSize: 19 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Chip
            avatar={<Avatar sx={{ background: isSuperAdmin ? 'linear-gradient(135deg, #dc2626, #f97316)' : 'linear-gradient(135deg, #3b82f6, #6366f1)', width: 28, height: 28, fontSize: 11 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>}
            label={`${user?.firstName || ''} ${user?.lastName || ''}`}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              cursor: 'pointer', fontWeight: 600, fontSize: 12.5, height: 36,
              bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          />
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { mt: 1, borderRadius: 3, minWidth: 180, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }} sx={{ py: 1.5, gap: 1.5, fontSize: 13 }}>
              <AccountCircle sx={{ fontSize: 18, color: '#64748b' }} /> Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }} sx={{ py: 1.5, gap: 1.5, color: '#ef4444', fontSize: 13 }}>
              <Logout sx={{ fontSize: 18 }} /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}
          open>
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{
        flexGrow: 1, p: { xs: 2, sm: 3 }, pt: { xs: 9, sm: 10 },
        width: { md: `calc(100% - ${drawerWidth}px)` },
        minHeight: '100vh', maxWidth: '100%', overflow: 'hidden'
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
